// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * One undo history for the whole project.
 *
 * There used to be five: one for the estimate grid and one each for the
 * schedule, logistics, submittals and bids, each routed by whichever tab was
 * open. So Ctrl+Z did a different thing depending on where you were standing,
 * and anything edited outside those five tabs -- escalation, markup, the
 * staffing plan, packages, exclusions, the whole of Setup -- was not undoable
 * at all. Change a percentage, walk away, and the only way back was to
 * remember the old number.
 *
 * So: one journal, every tab, every field. Ctrl+Z steps back through the
 * project's changes in the order they were made, from wherever you are now.
 *
 * Two things make that safe rather than alarming:
 *
 *  1. An undo that lands off-screen must SAY what it did. Every step is
 *     described from the two states either side of it -- "Escalation · Annual
 *     rate 3% -> 0%" -- and tagged with the tab it happened on. A silent undo
 *     of something you cannot see is worse than no undo, because afterwards
 *     you no longer know what the estimate says.
 *
 *  2. Steps share structure. A step clones only the slices that actually
 *     moved and reuses the previous step's clones for everything else, so
 *     holding sixty steps of a whole project costs about what sixty steps of
 *     one tab used to.
 */

/** A shallow bag of project state, keyed by the names in UNDOABLE_KEYS. */
export type Slices = Record<string, any>;

export interface HistoryEntry {
  /** The slices as they were BEFORE the change this entry steps back to. */
  state: Slices;
  /**
   * The references `state` was cloned from. Lets the next capture reuse a
   * clone for any slice that has not moved since -- see `capture`.
   */
  raw: Slices;
  /** Which tab was open when the change was made. */
  tab: string;
  /** Which slices changed. Consecutive edits to the same slices are one
   *  gesture and coalesce into a single step. */
  keys: string[];
  at: number;
}

export interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
}

export const emptyHistory = (): HistoryState => ({ past: [], future: [] });

/** How many steps back you can go. Sixty was the estimate grid's depth. */
export const MAX_HISTORY = 60;

// -- What is undoable -------------------------------------------------------

type Kind = "text" | "number" | "percent" | "area" | "flag" | "nested" | "list" | "record" | "opaque";

interface FieldSpec {
  label: string;
  kind: Kind;
  /** For `nested`: what to call each field inside. */
  children?: Record<string, { label: string; kind: Kind }>;
}

/**
 * Every slice of the project that Ctrl+Z moves, and what to call it.
 *
 * history.test.ts checks this against the project's own state list, so a
 * field added to a project cannot quietly become un-undoable, or print as a
 * raw property name in the toast.
 */
export const FIELDS: Record<string, FieldSpec> = {
  projectName:     { label: "Project name", kind: "text" },
  address:         { label: "Address", kind: "text" },
  buildingArea:    { label: "Building area", kind: "area" },
  duration:        { label: "Duration", kind: "number" },
  overhead:        { label: "Overhead", kind: "percent" },
  profit:          { label: "Profit", kind: "percent" },
  contingency:     { label: "Contingency", kind: "percent" },
  contingencyPolicy: { label: "Contingency", kind: "nested", children: {
    mode:                 { label: "Mode", kind: "text" },
    reservePct:           { label: "Reserve", kind: "percent" },
    base:                 { label: "Reserve base", kind: "text" },
    feeOnLineContingency: { label: "Fee on line contingency", kind: "flag" },
  } },
  escalation:      { label: "Escalation", kind: "nested", children: {
    annualPct:      { label: "Annual rate", kind: "percent" },
    monthsOverride: { label: "Midpoint override", kind: "number" },
  } },
  staffPlan:       { label: "Staffing plan", kind: "list" },
  markupMode:      { label: "Markup mode", kind: "text" },
  divisionMarkups: { label: "Division markups", kind: "record" },
  proposals:       { label: "Packages", kind: "list" },
  alternates:      { label: "Alternate packages", kind: "list" },
  proposalAreas:   { label: "Package areas", kind: "record" },
  divisionSettings:{ label: "Divisions", kind: "list" },
  subSettings:     { label: "Subcategories", kind: "record" },
  // Line items lead the estimate pair. Adding a row moves both this and the
  // division tree it hangs off; "Line items — 1 added" is what happened,
  // where "Estimate structure · “General Requirements”" is only where.
  itemState:       { label: "Line items", kind: "record" },
  divisions:       { label: "Estimate structure", kind: "list" },
  linkedBudgetId:  { label: "Linked budget", kind: "text" },
  bidAlignments:   { label: "Bid alignment", kind: "record" },
  bidScopes:       { label: "Bid packages", kind: "list" },
  bidPartners:     { label: "Bidders", kind: "list" },
  bids:            { label: "Bids", kind: "list" },
  levelingAdjustments: { label: "Levelling adjustments", kind: "list" },
  tradeSources:    { label: "Trade sources", kind: "list" },
  projectExclusions: { label: "Exclusions", kind: "list" },
  rfis:            { label: "RFIs", kind: "list" },
  buyoutItems:     { label: "Buyout", kind: "list" },
  schedule:        { label: "Schedule", kind: "nested", children: {
    projectStartDate: { label: "Start date", kind: "text" },
    totalDuration:    { label: "Duration", kind: "number" },
    durationUnit:     { label: "Duration unit", kind: "text" },
    phases:           { label: "Phases", kind: "list" },
  } },
  costSchedule:    { label: "Cost schedule", kind: "record" },
  submittals:      { label: "Submittals", kind: "list" },
  siteLogistics:   { label: "Site logistics", kind: "opaque" },
  historyNotes:    { label: "Comparison notes", kind: "record" },
  costSFCents:     { label: "Cost/SF precision", kind: "flag" },
  drawingList:     { label: "Drawing list", kind: "list" },
  projectNotes:    { label: "Notes", kind: "opaque" },
  openItems:       { label: "Open items", kind: "list" },
  jurisdictionInfo:{ label: "Jurisdiction", kind: "opaque" },
};

export const UNDOABLE_KEYS: readonly string[] = Object.keys(FIELDS);

/**
 * Parts of a project that Ctrl+Z deliberately leaves alone, and why.
 *
 * Written down rather than left silent, so "is this undoable?" has an answer
 * in the source instead of in whoever wrote it.
 */
export const NOT_UNDOABLE: Record<string, string> = {
  // Saving or deleting a milestone is a filing action, not an edit to the
  // estimate. On a cloud project those rows live in Supabase, so an undo
  // would put back a snapshot the server no longer has.
  projectSnapshots: "version history, not an edit",
  // A one-way, one-time data migration. Reversing the flag would run it twice.
  migratedOpenItems: "one-way migration flag",
};

// -- Capture ----------------------------------------------------------------

/**
 * A deep copy that shares every string.
 *
 * Strings are immutable, so two frames can point at the same one safely --
 * and one of them is a whole site plan. A logistics drawing is held as a
 * base64 data URL on `siteLogistics`, several megabytes of it, and copying
 * that on each step would have meant sixty copies of the same picture.
 * A hand-written walk gets this for free where structuredClone does not:
 * primitives are returned as they are.
 */
const clone = <T,>(v: T): T => {
  if (v === null || typeof v !== "object") return v;
  if (v instanceof Date) return new Date(v.getTime()) as unknown as T;
  if (Array.isArray(v)) return v.map(clone) as unknown as T;
  const out: Record<string, any> = {};
  for (const k of Object.keys(v as object)) out[k] = clone((v as any)[k]);
  return out as T;
};

// -- Scopes ------------------------------------------------------------------

export type ScopeId = "estimate" | "bids" | "schedule" | "logistics" | "submittals" | "rfis";

export interface Scope {
  /** Named in "nothing to undo in ___". */
  label: string;
  /** The slices this scope owns. */
  keys: string[];
  /** The tabs its changes can be SEEN from, and so undone from. */
  tabs: string[];
}

/**
 * Undo reaches as far as your eyes do, and no further.
 *
 * A history that spans the whole project sounds like a feature and is not.
 * Dragging a trailer across the site plan, walking to the Summary and
 * pressing Ctrl+Z would redraw a drawing you cannot see, on the word of a
 * toast -- which is the thing this was built to stop, not to do.
 *
 * So the journal is partitioned by what a change is visible in. The estimate
 * scope is wide because its tabs all show one number: set an escalation rate
 * in Setup and the Summary total moves, so undoing it from the Summary is
 * something you can watch happen. Each Planning tab is its own scope because
 * nothing outside it reflects what it holds -- a submittal log, an RFI log,
 * a bar chart, a drawing. Bids and Buyout share one, because awarding a bid
 * writes to both and an undo must not take back half of it.
 *
 * Entries still restore every slice they touched, whichever scope they are
 * filed under, so nothing is ever half-undone.
 */
export const SCOPES: Record<ScopeId, Scope> = {
  estimate: {
    label: "the estimate",
    keys: ["projectName", "address", "buildingArea", "duration", "overhead", "profit",
      "contingency", "contingencyPolicy", "escalation", "staffPlan", "markupMode",
      "divisionMarkups", "proposals", "alternates", "proposalAreas", "divisionSettings",
      "subSettings", "itemState", "divisions", "linkedBudgetId", "projectExclusions",
      "historyNotes", "costSFCents", "drawingList", "projectNotes", "openItems",
      "jurisdictionInfo"],
    tabs: ["summary", "decisions", "estimate", "alternates", "analysis", "scope", "history",
      "properties", "properties-defaults", "budget"],
  },
  bids: {
    label: "Bids",
    keys: ["bidAlignments", "bidScopes", "bidPartners", "bids", "levelingAdjustments",
      "tradeSources", "buyoutItems"],
    tabs: ["bids", "buyout"],
  },
  // Cash Flow draws the cost schedule rather than owning anything of its own,
  // so the two tabs share a history -- as they did before this was one journal.
  schedule:   { label: "the Schedule",       keys: ["schedule", "costSchedule"], tabs: ["schedule", "cashflow"] },
  logistics:  { label: "the Logistics plan", keys: ["siteLogistics"],            tabs: ["logistics"] },
  submittals: { label: "Submittals",         keys: ["submittals"],               tabs: ["submittals"] },
  rfis:       { label: "RFIs",               keys: ["rfis"],                     tabs: ["rfi"] },
};

export const SCOPE_IDS = Object.keys(SCOPES) as ScopeId[];

const KEY_SCOPE = new Map<string, ScopeId>(
  SCOPE_IDS.flatMap(id => SCOPES[id].keys.map(k => [k, id] as [string, ScopeId])));
const TAB_SCOPE = new Map<string, ScopeId>(
  SCOPE_IDS.flatMap(id => SCOPES[id].tabs.map(t => [t, id] as [string, ScopeId])));

/** Which history a slice belongs to. */
export const scopeOfKey = (key: string): ScopeId | null => KEY_SCOPE.get(key) ?? null;

/** Which history Ctrl+Z reaches from this tab, if any. */
export const scopeForTab = (tab: string): ScopeId | null => TAB_SCOPE.get(tab) ?? null;

/**
 * Where to file a change.
 *
 * UNDOABLE_KEYS runs estimate-first, so a change that somehow spanned two
 * scopes is filed under the estimate -- the scope reachable from the most
 * tabs, and so the one least likely to strand it somewhere unreachable.
 */
export const scopeOfChange = (keys: readonly string[]): ScopeId | null => {
  for (const k of UNDOABLE_KEYS) if (keys.includes(k)) { const s = scopeOfKey(k); if (s) return s; }
  return null;
};

/**
 * How many slices a single change has to touch before it is a project being
 * LOADED rather than somebody editing one.
 *
 * Every load path says so for itself -- see resetHistoryRef in OpenEstimate.
 * This is the backstop for the one that gets added later and forgets, and it
 * is worth having because of what that failure would be: an undo stepping
 * back into the project you had open before this one, and writing that job's
 * numbers over this job's. Nothing a person does in one action comes close.
 * The widest are an alternate package being accepted and a levelling import,
 * at three slices and four.
 */
export const LOAD_WIDTH = 10;

/** Too much moved at once for this to have been an edit. */
export const looksLikeLoad = (keys: readonly string[]) => keys.length >= LOAD_WIDTH;

/** Slice keys whose value differs between two bags. */
export function changedKeys(a: Slices, b: Slices, keys: readonly string[] = UNDOABLE_KEYS): string[] {
  return keys.filter(k => a?.[k] !== b?.[k]);
}

/**
 * Freeze a bag of slices for the journal.
 *
 * Pass only the slices that CHANGED. To step back you need what they were
 * before, and nothing else -- a step that also carried the thirty-five
 * untouched slices would copy the whole project every time somebody typed a
 * quantity. So an undo step costs what the edit it reverses was worth.
 */
export function capture(slices: Slices, keys: readonly string[]): { state: Slices; raw: Slices } {
  const state: Slices = {}, raw: Slices = {};
  for (const k of keys) {
    const v = slices?.[k];
    raw[k] = v;
    state[k] = clone(v);
  }
  return { state, raw };
}

/** Two changes touch the same slices, in the same order. */
export const sameKeys = (a: string[], b: string[]) =>
  a.length === b.length && a.every((k, i) => k === b[i]);

/**
 * Add a step.
 *
 * `coalesce` folds this change into the step before it when the same slices
 * are still being edited -- a number typed digit by digit, a slider dragged.
 * The OLDER state is the one kept, because that is where the gesture started
 * and where one Ctrl+Z should land you.
 *
 * Any new step drops the redo side: the future you stepped back from is no
 * longer the future.
 */
export function record(
  h: HistoryState,
  entry: HistoryEntry,
  opts: { coalesce?: boolean; max?: number } = {},
): HistoryState {
  const max = opts.max ?? MAX_HISTORY;
  const last = h.past[h.past.length - 1];
  if (opts.coalesce && last && sameKeys(last.keys, entry.keys)) {
    return { past: [...h.past.slice(0, -1), { ...last, at: entry.at }], future: [] };
  }
  const past = [...h.past, entry];
  return { past: past.length > max ? past.slice(past.length - max) : past, future: [] };
}

/** Step back one. `current` is what is on screen now, kept for the redo side. */
export function stepBack(
  h: HistoryState,
  current: { state: Slices; raw: Slices },
): { history: HistoryState; entry: HistoryEntry } | null {
  const entry = h.past[h.past.length - 1];
  if (!entry) return null;
  const forward: HistoryEntry = { ...current, tab: entry.tab, keys: entry.keys, at: Date.now() };
  return { history: { past: h.past.slice(0, -1), future: [...h.future, forward] }, entry };
}

/** Step forward one. */
export function stepForward(
  h: HistoryState,
  current: { state: Slices; raw: Slices },
): { history: HistoryState; entry: HistoryEntry } | null {
  const entry = h.future[h.future.length - 1];
  if (!entry) return null;
  const back: HistoryEntry = { ...current, tab: entry.tab, keys: entry.keys, at: Date.now() };
  return { history: { past: [...h.past, back], future: h.future.slice(0, -1) }, entry };
}

// -- Describing a step ------------------------------------------------------

const fmtNum = (n: number): string => {
  const r = Math.round(n * 100) / 100;
  return Math.abs(r) >= 1000 ? r.toLocaleString("en-US") : String(r);
};

const fmtValue = (v: any, kind: Kind): string => {
  if (v === null || v === undefined || v === "") return "—";
  switch (kind) {
    case "percent": return typeof v === "number" ? `${fmtNum(v)}%` : String(v);
    case "area":    return typeof v === "number" ? `${fmtNum(v)} SF` : String(v);
    case "number":  return typeof v === "number" ? fmtNum(v) : String(v);
    case "flag":    return v ? "on" : "off";
    default: {
      const s = String(v);
      return s.length > 40 ? `${s.slice(0, 37)}…` : s;
    }
  }
};

/** "annualPct" -> "Annual pct". Only reached for fields with no spec. */
const humanize = (key: string): string => {
  const s = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/**
 * Equal by value, not by identity.
 *
 * Two jobs. Every frame in the journal is a clone, so two frames never share
 * an object even where nothing moved: comparing them by reference reported a
 * single changed quantity as "91 line items edited".
 *
 * And a new object does not mean a new value. Leaving a cell commits it
 * whether or not you changed anything, which hands back a fresh itemState
 * holding exactly what it held before. Filed by reference, that is a step in
 * the history that undoes nothing -- press Ctrl+Z, watch nothing happen,
 * stop believing the feature.
 *
 * It short-circuits on identity at every level, so the common case -- one
 * entry of a large map replaced -- costs a walk of the map's keys and a real
 * comparison of the single entry that moved.
 */
/**
 * Nothing: no property, or a property holding nothing.
 *
 * Zero and false are values; "" and null and undefined are the absence of
 * one, and this project writes all three for the same thing. Committing a
 * cell with no formula runs `upd(id,"unitCostFormula", f||"")`, putting an
 * empty string where the key had simply been absent -- counted as a change,
 * that left an undo step behind every cell a Tab passed through, each one
 * doing visibly nothing when it came back round.
 */
const isEmpty = (v: any) => v === undefined || v === null || v === "";

export const sameValue = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (isEmpty(a) && isEmpty(b)) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  const aArr = Array.isArray(a), bArr = Array.isArray(b);
  if (aArr !== bArr) return false;
  if (aArr) return a.length === b.length && a.every((x: any, i: number) => sameValue(x, b[i]));
  const ka = Object.keys(a).filter(k => !isEmpty(a[k]));
  const kb = Object.keys(b).filter(k => !isEmpty(b[k]));
  return ka.length === kb.length && ka.every(k => sameValue(a[k], b[k]));
};

/** What a thing in a list or record calls itself, if it says. */
const nameOf = (v: any): string | null => {
  if (!v || typeof v !== "object") return null;
  const n = v.description ?? v.name ?? v.label ?? v.title;
  return typeof n === "string" && n.trim() ? n.trim() : null;
};

const guessKind = (key: string, v: any): Kind => {
  if (typeof v === "boolean") return "flag";
  if (typeof v === "number") return /pct|percent|rate$/i.test(key) ? "percent" : "number";
  return "text";
};

/**
 * The first leaf field of an object that moved: "Qty 10 -> 12", and how many
 * others went with it.
 *
 * The count is handed back rather than appended so the caller can put it
 * after whatever it adds of its own -- "Qty 47 -> 18 on “Slab” +1 more"
 * reads; "Qty 47 -> 18 +1 more on “Slab”" does not.
 */
function describeLeaves(
  from: any, to: any, children?: Record<string, { label: string; kind: Kind }>,
): { text: string; more: number } | null {
  if (!from || !to || typeof from !== "object" || typeof to !== "object") return null;
  const flat = (x: any) => x === null || x === undefined || typeof x !== "object";
  let moved = [...new Set([...Object.keys(from), ...Object.keys(to)])]
    .filter(k => from[k] !== to[k] && flat(from[k]) && flat(to[k]));
  // A cell holding "12*4" writes both `qty` and `qtyFormula`. That is one
  // edit told twice, and counting it as two makes every typed formula read
  // as though something else moved as well.
  moved = moved.filter(k => !(/Formula$/.test(k) && moved.includes(k.replace(/Formula$/, ""))));
  if (!moved.length) return null;
  const k = moved[0];
  const spec = children?.[k];
  const label = spec?.label ?? humanize(k);
  const kind = spec?.kind ?? guessKind(k, to[k] ?? from[k]);
  return { text: `${label} ${fmtValue(from[k], kind)} → ${fmtValue(to[k], kind)}`, more: moved.length - 1 };
}

/** "<label> · <leaf> on “<who>” +N more", with whatever is absent left out. */
const joinLeaf = (label: string, leaf: { text: string; more: number }, who: string | null) =>
  `${label} · ${leaf.text}${who ? ` on “${who}”` : ""}${leaf.more ? ` +${leaf.more} more` : ""}`;

const countPhrase = (added: number, removed: number, edited: number): string => {
  const parts: string[] = [];
  if (added) parts.push(`${added} added`);
  if (removed) parts.push(`${removed} removed`);
  if (edited) parts.push(`${edited} edited`);
  return parts.join(", ") || "changed";
};

/**
 * What is in `b` and not in `a`.
 *
 * Naming the last element instead was wrong the moment a list grew anywhere
 * but at the end -- undoing the deletion of the first bid package put it
 * back in its own place and then announced the name of the last one.
 *
 * Ids when every element has one, which is the cheap case and the usual
 * one; otherwise by value.
 */
function newcomers(a: any[], b: any[]): any[] {
  const ids = new Set(a.map(x => x?.id).filter((i: any): i is string => typeof i === "string"));
  if (ids.size === a.length) return b.filter(x => typeof x?.id === "string" && !ids.has(x.id));
  return b.filter(x => !a.some(y => sameValue(x, y)));
}

function describeList(label: string, from: any, to: any): string {
  const a = Array.isArray(from) ? from : [], b = Array.isArray(to) ? to : [];
  if (b.length === a.length) {
    const moved = a.map((_, i) => i).filter(i => !sameValue(a[i], b[i]));
    if (moved.length === 1) {
      const i = moved[0];
      const who = nameOf(b[i]) ?? nameOf(a[i]);
      const leaf = describeLeaves(a[i], b[i]);
      if (leaf) return joinLeaf(label, leaf, who);
      if (who) return `${label} · “${who}”`;
    }
    return `${label} — ${countPhrase(0, 0, moved.length || 1)}`;
  }
  const d = b.length - a.length;
  if (d > 0) {
    const gained = newcomers(a, b);
    const one = gained.length === 1 ? nameOf(gained[0]) : null;
    return one ? `${label} · added “${one}”` : `${label} — ${countPhrase(d, 0, 0)}`;
  }
  return `${label} — ${countPhrase(0, -d, 0)}`;
}

function describeRecord(label: string, from: any, to: any): string {
  const a = from && typeof from === "object" ? from : {};
  const b = to && typeof to === "object" ? to : {};
  let added = 0, removed = 0;
  const edited: string[] = [];
  for (const k of [...new Set([...Object.keys(a), ...Object.keys(b)])]) {
    if (!(k in a)) added++;
    else if (!(k in b)) removed++;
    else if (!sameValue(a[k], b[k])) edited.push(k);
  }
  if (!added && !removed && edited.length === 1) {
    const k = edited[0];
    const who = nameOf(b[k]) ?? nameOf(a[k]);
    const leaf = describeLeaves(a[k], b[k]);
    if (leaf) return joinLeaf(label, leaf, who);
    if (who) return `${label} · “${who}”`;
  }
  return `${label} — ${countPhrase(added, removed, edited.length)}`;
}

/** One slice, described. */
function describeKey(key: string, from: any, to: any): string {
  const spec = FIELDS[key] ?? { label: humanize(key), kind: "opaque" as Kind };
  switch (spec.kind) {
    case "text": case "number": case "percent": case "area": case "flag":
      return `${spec.label} ${fmtValue(from, spec.kind)} → ${fmtValue(to, spec.kind)}`;
    case "nested": {
      const leaf = describeLeaves(from, to, spec.children);
      if (leaf) return joinLeaf(spec.label, leaf, null);
      // A nested object whose only movement is in a list inside it. Compared
      // by value: every frame is a clone, so by reference they ALL moved.
      for (const [k, c] of Object.entries(spec.children ?? {})) {
        if (c.kind === "list" && !sameValue(from?.[k], to?.[k]))
          return describeList(`${spec.label} · ${c.label}`, from?.[k], to?.[k]);
      }
      return `${spec.label} changed`;
    }
    case "list":   return describeList(spec.label, from, to);
    case "record": return describeRecord(spec.label, from, to);
    default:       return `${spec.label} changed`;
  }
}

/**
 * What changed between two states, in a sentence.
 *
 * Computed when the undo happens rather than when the edit does: it costs
 * nothing until somebody presses the key, and it then describes what is
 * actually about to move rather than what was intended at the time.
 */
export function describeChange(from: Slices, to: Slices, keys: readonly string[] = UNDOABLE_KEYS): string {
  const moved = changedKeys(from, to, keys);
  if (!moved.length) return "No change";
  // A slice can be a new object holding the same values -- a map() over a
  // list that changed nothing. Those are not worth reporting, so they are
  // only fallen back on when nothing moved by value at all.
  const real = moved.filter(k => !sameValue(from[k], to[k]));
  const list = real.length ? real : moved;
  // Whichever slice is listed first wins the headline: FIELDS runs roughly
  // from the project's own settings down to its logs, which is the order a
  // reader wants to hear about them in.
  const lead = describeKey(list[0], from[list[0]], to[list[0]]);
  return list.length > 1 ? `${lead} +${list.length - 1} more` : lead;
}
